import { assertNever } from '../lib/assert-never.js';
import type {
  DisplayCondition,
  ExperiencePage,
  ExperiencePageDraft,
} from '../schemas/experience.js';
import { NOYAU_VARIABLE_ID, type NoyauCode } from '../schemas/noyau.js';
import type { ObjectiveStatus } from '../schemas/protocol.js';
import type { SessionValues } from '../schemas/session-play.js';
import type { ExperienceVariable } from './variables.js';

/**
 * The EVALUATION of the display conditions of §9.2 of the refonte (revue du
 * 2026-09-25; noyau.md §3.6) - « ce bloc est-il là ».
 *
 * ## One function, two players (H72)
 *
 * The reader plays it when a page is shown, the server plays it at the advance,
 * on the values of the session: a masked required field greys no button and
 * refuses no advance, at the browser AND at the server. Two spellings of this
 * question would put the two in disagreement on exactly that field - a button
 * the browser arms and an advance the server refuses. So it is pure, in
 * `shared`, and takes everything it reads as an argument.
 *
 * ## What an EMPTY variable does (H71)
 *
 * « Une variable vide rend la condition fausse - un choix non fait, un noyau
 * absent parce que la séquence a été sautée, un champ jamais rempli - sauf « est
 * vide », qui est alors vraie. » Read per nature, the exception is the operator
 * that ASKS whether the variable is empty, and each nature has one:
 *
 * - `noyau` and `choice` (« est l'un de ») - no retained noyau, no choice made:
 *   false. There is no « est vide » here.
 * - `input` - absent or blank is empty: `empty` is true, `filled` false.
 * - `checkbox` - a box nobody ticked is UNTICKED: `unchecked` is true, `checked`
 *   false. It has to be: the reader writes a checkbox's value only when it is
 *   TOGGLED, so a box the person saw and left alone is as absent from the values
 *   as one they never reached - and the maquette says it in so many words, « une
 *   case jamais atteinte vaut « n'est pas cochée » » (ConditionsParNature).
 * - `objective` - « réussi » counts the passable (H68), since a passable
 *   objective IS `succeeded`; « n'est pas réussi » counts the failed and the
 *   empty, and an objective the session holds no status for is empty (§7.3).
 */

/**
 * What a condition is evaluated against: the state of ONE session, or of one
 * preview that simulates it (H73).
 */
export type DisplayConditionSources = {
  /** The values of the session: by block id for a field, by page id for a choice (§8.6). */
  readonly values: SessionValues;
  /** The RETAINED noyau - null before the first choice, and when the sequence was skipped. */
  readonly noyau: NoyauCode | null;
  /** The status of each objective of the frozen protocol; an absent one is `empty`. */
  readonly objectives: Readonly<Record<string, ObjectiveStatus>>;
  /**
   * The pages of the content the session plays - the FROZEN version, or the
   * draft a preview simulates. What a `choice` condition reads its buttons'
   * labels off, since the session retains the label pressed and the condition
   * names the buttons by id.
   */
  readonly pages: readonly (ExperiencePage | ExperiencePageDraft)[];
};

/**
 * The variable a condition cites, as every rule on variables takes it
 * (`ExperienceVariable`): what the reach rule, the resolution and the palette
 * read. A Checkbox and an Input are both a `{{champ:<blockId>}}`.
 */
export function displayConditionVariable(condition: DisplayCondition): ExperienceVariable {
  switch (condition.kind) {
    case 'noyau':
      return { kind: 'noyau', id: NOYAU_VARIABLE_ID };
    case 'choice':
      return { kind: 'choice', id: condition.pageId };
    case 'checkbox':
    case 'input':
      return { kind: 'field', id: condition.blockId };
    case 'objective':
      return { kind: 'objective', id: condition.objectiveId };
    default:
      return assertNever(condition, 'display condition');
  }
}

/**
 * The labels of the named buttons of one page that LEAVE it, in the content
 * the session plays. A Bouton « temps suivant » never ends its page, so it
 * never writes the choice (§8.6): it gives no label here, as the save refuses a
 * condition naming it (`unresolved-variable`) - one reading of « which press
 * wrote this value » for the evaluation and for the refusal.
 */
function buttonLabels(
  pages: DisplayConditionSources['pages'],
  pageId: string,
  buttonIds: readonly string[],
): string[] {
  const page = pages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return [];
  return page.temps
    .flatMap((temps) => temps.blocks)
    .flatMap((block) =>
      block.type === 'button' && block.action === 'next-page' && buttonIds.includes(block.id)
        ? [block.label]
        : [],
    );
}

/** Whether ONE condition is true on these sources - see the head of this file. */
export function displayConditionHolds(
  condition: DisplayCondition,
  sources: DisplayConditionSources,
): boolean {
  switch (condition.kind) {
    case 'noyau':
      return sources.noyau !== null && condition.noyaux.includes(sources.noyau);
    case 'choice': {
      const chosen = sources.values[condition.pageId];
      if (typeof chosen !== 'string' || chosen === '') return false;
      return buttonLabels(sources.pages, condition.pageId, condition.buttonIds).includes(chosen);
    }
    case 'checkbox': {
      const ticked = sources.values[condition.blockId] === true;
      return condition.operator === 'checked' ? ticked : !ticked;
    }
    case 'input': {
      const value = sources.values[condition.blockId];
      const filled = typeof value === 'string' && value.trim() !== '';
      return condition.operator === 'filled' ? filled : !filled;
    }
    case 'objective': {
      const succeeded = sources.objectives[condition.objectiveId] === 'succeeded';
      return condition.operator === 'succeeded' ? succeeded : !succeeded;
    }
    default:
      return assertNever(condition, 'display condition');
  }
}

/**
 * Whether a block SHOWS: every one of its conditions holds - the « et » between
 * conditions - and the empty list, the default, is « toujours affiché ».
 *
 * A masked block does not exist for the page (H70): it does not appear, does
 * not animate, produces nothing, and a required one refuses nothing. Walking a
 * page with this is the reader's and the server's business; this answers for
 * one block.
 */
export function isBlockDisplayed(
  block: { readonly conditions: readonly DisplayCondition[] },
  sources: DisplayConditionSources,
): boolean {
  return block.conditions.every((condition) => displayConditionHolds(condition, sources));
}
